// Themed dialog replacing window.confirm(). The native dialog is drawn by the browser, cannot be
// styled, and in a full-screen game reads as a page error rather than part of the product.
//
// useConfirm() keeps the ergonomics of window.confirm (an awaited boolean) so call sites stay
// simple, but the promise resolves from React state instead of blocking the main thread.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  title: string;
  children?: React.ReactNode;
  onClose: () => void;
  /** action buttons, rendered right-aligned in the footer */
  actions?: React.ReactNode;
  /** widen for content-heavy dialogs */
  wide?: boolean;
}

export function Modal({ open, title, children, onClose, actions, wide }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    restoreFocus.current = document.activeElement as HTMLElement | null;
    // Focus the panel so Esc and the focus trap work without requiring a click first.
    panelRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], input:not(:disabled), select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      restoreFocus.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="modal-scrim glass" onPointerDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div
        ref={panelRef}
        className={`modal-panel${wide ? ' wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <header className="modal-head">{title}</header>
        {children && <div className="modal-body">{children}</div>}
        <footer className="modal-actions">{actions}</footer>
      </div>
    </div>,
    document.body,
  );
}

interface ConfirmRequest {
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  resolve: (ok: boolean) => void;
}

/**
 * Drop-in replacement for window.confirm.
 *
 *   const [confirm, confirmDialog] = useConfirm();
 *   if (!(await confirm({ title: 'Discard changes?' }))) return;
 *   ...
 *   return <>{confirmDialog}</>;
 */
export function useConfirm(): [
  (request: Omit<ConfirmRequest, 'resolve'>) => Promise<boolean>,
  React.ReactNode,
] {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);

  const confirm = useCallback(
    (options: Omit<ConfirmRequest, 'resolve'>) =>
      new Promise<boolean>((resolve) => setRequest({ ...options, resolve })),
    [],
  );

  const settle = useCallback((ok: boolean) => {
    setRequest((current) => {
      current?.resolve(ok);
      return null;
    });
  }, []);

  const node = (
    <Modal
      open={request !== null}
      title={request?.title ?? ''}
      onClose={() => settle(false)}
      actions={
        <>
          <button className="btn small" onClick={() => settle(false)}>
            {request?.cancelLabel ?? 'Cancel'}
          </button>
          <button
            className={`btn small${request?.danger ? ' danger' : ''}`}
            onClick={() => settle(true)}
            autoFocus
          >
            {request?.confirmLabel ?? 'Confirm'}
          </button>
        </>
      }
    >
      {request?.body}
    </Modal>
  );

  return [confirm, node];
}
